// src/services/StoreInitializer.js - Centralized Store Initialization
// 🎯 PURPOSE: Initialize all Zustand stores once at app startup instead of per-component

// Import all store initialization functions
import { initializeCustomerStoreSocketListeners } from '../stores/CustomerStore';
import { initializeInvoiceStoreSocketListeners } from '../stores/InvoiceStore';
import { initializeEventsStoreSocketListeners } from '../stores/EventsStore';
import { initializeTeamStoreSocketListeners } from '../stores/TeamStore';
import { initializePartnersStoreSocketListeners } from '../stores/PartnersStore';
import { initializeSettingsStoreSocketListeners } from '../stores/SettingsStore';
import { initializeWidgetStoreSocketListeners } from '../stores/WidgetStore';
import { initializeStripeStoreSocketListeners } from '../stores/StripeStore';
import { initializeWorkflowStoreSocketListeners } from '../stores/WorkflowStore';
import { initializeGBPStoreSocketListeners } from '../stores/GBPStore';
// import { initializeAIConversationStoreSocketListeners } from '../stores/AIConversationStore';
import { initializeClientSiteStoreSocketListeners } from '../stores/ClientSiteStore';
import { initializeCalendarStoreSocketListeners } from '../stores/CalendarStore';
import { initializeTeamsStoreSocketListeners } from '../stores/TeamsStore';
import { initializeMicrosoftStoreSocketListeners } from '../stores/MicrosoftStore';
import { initializeZoomStoreSocketListeners } from '../stores/ZoomStore';
import { initializeDashboardStoreSocketListeners } from '../stores/DashboardStore';
import { initializeEntryStoreSocketListeners } from '../stores/EntryStore';
import { initializeNoteStoreSocketListeners } from '../stores/NoteStore';
import { initializeDocumentStoreSocketListeners } from '../stores/DocumentStore';
import { initializeTagStoreSocketListeners } from '../stores/TagStore';

/**
 * Centralized Store Initializer
 * 
 * Replaces the per-hook initialization pattern with a single app-startup initialization.
 * This eliminates duplicate initialization logs and improves component mounting performance.
 */
class StoreInitializer {
  constructor() {
    this.initialized = false;
    this.initializationPromise = null;
    this.initializedStores = new Set();
    this.failedStores = new Set();
    this.startTime = null;
    
    // ADDED: Set up auth reset listener
    this._setupAuthResetListener();
  }

  /**
   * ADDED: Set up listener for auth reset events
   * @private
   */
  _setupAuthResetListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('auth-state-reset', () => {
        console.log('🧹 StoreInitializer: Auth reset detected, resetting initialization state');
        this.reset();
      });
    }
  }

  /**
   * Initialize all stores with socket event listeners
   * @param {object} socketManager - The SocketManager instance
   * @returns {Promise<object>} - Initialization results
   */
  async initializeAllStores(socketManager) {
    // Prevent multiple concurrent initializations
    if (this.initializationPromise) {
      console.log('🔄 StoreInitializer: Initialization already in progress, waiting...');
      return this.initializationPromise;
    }

    // 🚨 FIXED: Remove the early return that was causing the issue
    // The problem was this check was preventing re-initialization after auth reset
    // if (this.initialized) {
    //   console.log('✅ StoreInitializer: Stores already initialized');
    //   return { ... };
    // }

    // Validate socket manager
    if (!socketManager?.universalHandler) {
      const error = 'Socket manager or universal handler not available';
      console.error('❌ StoreInitializer:', error);
      return {
        success: false,
        error,
        initializedStores: [],
        failedStores: [],
        totalTime: 0
      };
    }

    // Create and store the initialization promise
    this.initializationPromise = this._performInitialization(socketManager);
    
    try {
      const result = await this.initializationPromise;
      this.initialized = true;
      return result;
    } catch (error) {
      console.error('❌ StoreInitializer: Initialization failed:', error);
      return {
        success: false,
        error: error.message,
        initializedStores: Array.from(this.initializedStores),
        failedStores: Array.from(this.failedStores),
        totalTime: this.startTime ? Date.now() - this.startTime : 0
      };
    } finally {
      this.initializationPromise = null;
    }
  }

  /**
   * Internal method to perform the actual initialization
   * @private
   */
  async _performInitialization(socketManager) {
    this.startTime = Date.now();
    console.log('🚀 StoreInitializer: Beginning store initialization...');

    // 🚨 FIXED: Clear previous state before initializing
    this.initializedStores.clear();
    this.failedStores.clear();

    // Define all stores to initialize with their names and functions
    const stores = [
      { name: 'CustomerStore', fn: initializeCustomerStoreSocketListeners },
      { name: 'InvoiceStore', fn: initializeInvoiceStoreSocketListeners },
      { name: 'EventsStore', fn: initializeEventsStoreSocketListeners },
      { name: 'TeamStore', fn: initializeTeamStoreSocketListeners },
      { name: 'PartnersStore', fn: initializePartnersStoreSocketListeners },
      { name: 'SettingsStore', fn: initializeSettingsStoreSocketListeners },
      { name: 'WidgetStore', fn: initializeWidgetStoreSocketListeners },
      { name: 'StripeStore', fn: initializeStripeStoreSocketListeners },
      { name: 'WorkflowStore', fn: initializeWorkflowStoreSocketListeners },
      { name: 'GBPStore', fn: initializeGBPStoreSocketListeners },
      // { name: 'AIConversationStore', fn: initializeAIConversationStoreSocketListeners },
      { name: 'ClientSiteStore', fn: initializeClientSiteStoreSocketListeners },
      { name: 'CalendarStore', fn: initializeCalendarStoreSocketListeners },
      { name: 'TeamsStore', fn: initializeTeamsStoreSocketListeners },
      { name: 'MicrosoftStore', fn: initializeMicrosoftStoreSocketListeners },
      { name: 'ZoomStore', fn: initializeZoomStoreSocketListeners },
      { name: 'DashboardStore', fn: initializeDashboardStoreSocketListeners },
      { name: 'EntryStore', fn: initializeEntryStoreSocketListeners },
      { name: 'NoteStore', fn: initializeNoteStoreSocketListeners },
      { name: 'DocumentStore', fn: initializeDocumentStoreSocketListeners },
      { name: 'TagStore', fn: initializeTagStoreSocketListeners }
    ];

    // Initialize each store with error handling
    for (const store of stores) {
      try {
        console.log(`📦 StoreInitializer: Initializing ${store.name}...`);
        
        // Call the store's initialization function
        await store.fn(socketManager);
        
        this.initializedStores.add(store.name);
        console.log(`✅ StoreInitializer: ${store.name} initialized successfully`);
        
      } catch (error) {
        this.failedStores.add(store.name);
        console.error(`❌ StoreInitializer: ${store.name} initialization failed:`, error);
        
        // Don't throw here - we want to try initializing other stores
        // Store the error for reporting but continue
      }
    }

    const totalTime = Date.now() - this.startTime;
    const successCount = this.initializedStores.size;
    const failureCount = this.failedStores.size;

    // Log summary
    if (failureCount === 0) {
      console.log(`🎉 StoreInitializer: All ${successCount} stores initialized successfully in ${totalTime}ms`);
    } else {
      console.warn(`⚠️ StoreInitializer: ${successCount} stores initialized, ${failureCount} failed in ${totalTime}ms`);
      console.warn('Failed stores:', Array.from(this.failedStores));
    }

    return {
      success: failureCount === 0,
      initializedStores: Array.from(this.initializedStores),
      failedStores: Array.from(this.failedStores),
      totalTime,
      successCount,
      failureCount
    };
  }

  /**
   * Get initialization status
   * @returns {object} Current status
   */
  getStatus() {
    return {
      initialized: this.initialized,
      initializing: !!this.initializationPromise,
      initializedStores: Array.from(this.initializedStores),
      failedStores: Array.from(this.failedStores),
      totalStores: 22
    };
  }

  /**
   * Check if a specific store is initialized
   * @param {string} storeName - Name of the store to check
   * @returns {boolean}
   */
  isStoreInitialized(storeName) {
    return this.initializedStores.has(storeName);
  }

  /**
   * Check if all stores are initialized
   * @returns {boolean}
   */
  areAllStoresInitialized() {
    return this.initialized && this.initializedStores.size === 22;
  }

  /**
   * Get list of stores that failed to initialize
   * @returns {Array<string>}
   */
  getFailedStores() {
    return Array.from(this.failedStores);
  }

  /**
   * Reset the initializer (useful for testing or re-initialization)
   */
  reset() {
    console.log('🔄 StoreInitializer: Resetting state');
    this.initialized = false;
    this.initializationPromise = null;
    this.initializedStores.clear();
    this.failedStores.clear();
    this.startTime = null;
  }

  /**
   * Manual fallback initialization for a specific store
   * Useful if app-level initialization fails but component needs the store
   * @param {string} storeName - Name of the store
   * @param {object} socketManager - Socket manager instance
   */
  async initializeStore(storeName, socketManager) {
    if (this.initializedStores.has(storeName)) {
      console.log(`✅ StoreInitializer: ${storeName} already initialized`);
      return true;
    }

    const storeMap = {
      'CustomerStore': initializeCustomerStoreSocketListeners,
      'InvoiceStore': initializeInvoiceStoreSocketListeners,
      'EventsStore': initializeEventsStoreSocketListeners,
      'TeamStore': initializeTeamStoreSocketListeners,
      'PartnersStore': initializePartnersStoreSocketListeners,
      'SettingsStore': initializeSettingsStoreSocketListeners,
      'WidgetStore': initializeWidgetStoreSocketListeners,
      'StripeStore': initializeStripeStoreSocketListeners,
      'WorkflowStore': initializeWorkflowStoreSocketListeners,
      'GBPStore': initializeGBPStoreSocketListeners,
      // 'AIConversationStore': initializeAIConversationStoreSocketListeners,
      'ClientSiteStore': initializeClientSiteStoreSocketListeners,
      'CalendarStore': initializeCalendarStoreSocketListeners,
      'TeamsStore': initializeTeamsStoreSocketListeners,
      'MicrosoftStore': initializeMicrosoftStoreSocketListeners,
      'ZoomStore': initializeZoomStoreSocketListeners,
      'DashboardStore': initializeDashboardStoreSocketListeners,
      'EntryStore': initializeEntryStoreSocketListeners,
      'NoteStore': initializeNoteStoreSocketListeners,
      'DocumentStore': initializeDocumentStoreSocketListeners,
      'TagStore': initializeTagStoreSocketListeners
    };

    const initFn = storeMap[storeName];
    if (!initFn) {
      console.error(`❌ StoreInitializer: Unknown store name: ${storeName}`);
      return false;
    }

    try {
      console.log(`🔧 StoreInitializer: Fallback initialization for ${storeName}`);
      await initFn(socketManager);
      this.initializedStores.add(storeName);
      this.failedStores.delete(storeName); // Remove from failed if it was there
      console.log(`✅ StoreInitializer: ${storeName} fallback initialization successful`);
      return true;
    } catch (error) {
      console.error(`❌ StoreInitializer: ${storeName} fallback initialization failed:`, error);
      this.failedStores.add(storeName);
      return false;
    }
  }
}

// Create singleton instance
const storeInitializer = new StoreInitializer();

export default storeInitializer;